/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import {
    Box,
    Stack,
    ButtonGroup,
    Button
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'
import {getPDPath} from '../../../utils/custom-utils'

/**
 * Hero Banner component
 * @returns {React.ReactElement} - HeroBanner component.
 */
export const HeroBanner = ({
    custom,
    imageAltTag,
    backgroundColor,
    heroText,
    heroHeadingfontSIze,
    heroTextColor,
    heroTextStyle,
    heroTextfontWeight,
    heroSubHeadingText,
    heroSubHeadingfontSIze,
    heroSubHeadingTextColor,
    heroSubHeadingStyle,
    heroSubHeadingfontWeight,
    buttonBackgroundColor,
    buttonRoundedcorners,
    buttonfontWeight,
    buttonfontStyle,
    buttonTextColor,
    buttonText,
    categoryLink,
    containerPosition,
    target
}) => {
    const {site, locale} = useMultiSite()
    const categoryUrl = getPDPath(categoryLink, site, locale)
    const desktopImageUrl = custom?.desktopCategoryImage?.disBaseLink
    const mobileImageUrl = custom?.mobileCategoryImage?.disBaseLink || desktopImageUrl
    const targetLink = target === 'New-Window' ? '_blank' : '_self'
    const segment = useSegment()

    return (
        <Box maxWidth={{base: '100%', sm: '100%', md: '100%', lg: '100%', xl: '1920px'}} m="0 auto">
            <Helmet>
                {/* Preload desktop image */}
                {desktopImageUrl && (
                    <link
                        rel="preload"
                        href={desktopImageUrl}
                        as="image"
                        media="(min-width: 48em)"
                    />
                )}

                {/* Preload mobile image */}
                {mobileImageUrl && (
                    <link
                        rel="preload"
                        href={mobileImageUrl}
                        as="image"
                        media="(max-width: 47.99em)"
                    />
                )}
            </Helmet>
            <Box
                as="figure"
                className="image-with-text-figure"
                pos="relative"
                onClick={(event) => {
                    // segment
                    segment.sendPanelSelectedEvent(event, {})
                }}
            >
                <Link to={categoryUrl} target={targetLink} display="block">
                    <picture>
                        <source srcSet={desktopImageUrl} media="(min-width: 48em)" />
                        <source srcSet={mobileImageUrl} media="(max-width: 47.99em)" />
                        {/* Fallback image - this will only be used if no source matches or for browsers that don't support picture */}
                        <img
                            className="image-with-text-image"
                            data-testid="image-with-text-image"
                            src={desktopImageUrl}
                            alt={imageAltTag}
                            loading="eager"
                        />
                    </picture>
                </Link>

                {/* This is the block you need to change, to customize the caption */}
                <Stack
                    className={`hero-container ${containerPosition}`}
                    gap="0"
                    backgroundColor={`${backgroundColor?.value}`}
                >
                    <Box
                        className="hero-text"
                        style={{
                            fontWeight: heroTextfontWeight,
                            fontStyle: heroTextStyle,
                            color: heroTextColor?.value
                        }}
                        fontSize={{
                            base: '20px',
                            sm: heroHeadingfontSIze
                        }}
                    >
                        {heroText}
                    </Box>

                    <Box
                        className="hero-sub-text"
                        style={{
                            fontWeight: heroSubHeadingfontWeight,
                            fontStyle: heroSubHeadingStyle,
                            color: heroSubHeadingTextColor?.value
                        }}
                        fontSize={{
                            base: '12px',
                            sm: heroSubHeadingfontSIze
                        }}
                        sx={{
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2
                        }}
                    >
                        {heroSubHeadingText}
                    </Box>

                    {buttonText && (
                        <ButtonGroup gap="4" justifyContent="center">
                            <Button
                                variant="primary"
                                backgroundColor={`${buttonBackgroundColor?.value}`}
                                className={`${
                                    buttonRoundedcorners == 'Yes' ? 'rounded-corners' : ''
                                } shop-now-btn`}
                                style={{
                                    fontWeight: buttonfontWeight,
                                    fontStyle: buttonfontStyle,
                                    color: heroSubHeadingTextColor?.value
                                }}
                            >
                                <Link
                                    style={{
                                        color: buttonTextColor?.value
                                    }}
                                    href={categoryUrl}
                                    target={targetLink}
                                >
                                    {buttonText}
                                </Link>
                            </Button>
                        </ButtonGroup>
                    )}
                </Stack>
            </Box>
        </Box>
    )
}

HeroBanner.propTypes = {
    custom: PropTypes.object,
    imageAltTag: PropTypes.string,
    backgroundColor: PropTypes.shape({
        value: PropTypes.string
    }),
    heroText: PropTypes.string,
    heroTextColor: PropTypes.shape({
        value: PropTypes.string
    }),
    heroTextStyle: PropTypes.string,
    heroHeadingfontSIze: PropTypes.string,
    heroTextfontWeight: PropTypes.string,
    heroSubHeadingText: PropTypes.string,
    heroSubHeadingTextColor: PropTypes.shape({
        value: PropTypes.string
    }),
    heroSubHeadingStyle: PropTypes.string,
    heroSubHeadingfontWeight: PropTypes.string,
    heroSubHeadingfontSIze: PropTypes.string,
    buttonText: PropTypes.string,
    buttonBackgroundColor: PropTypes.shape({
        value: PropTypes.string
    }),
    buttonTextColor: PropTypes.shape({
        value: PropTypes.string
    }),
    buttonfontStyle: PropTypes.string,
    buttonfontWeight: PropTypes.string,
    buttonRoundedcorners: PropTypes.string,
    categoryLink: PropTypes.string,
    containerPosition: PropTypes.string,
    target: PropTypes.string
}

export default HeroBanner
